/**
 * 言語別の Polly 音声設定 (`BroadcastPoint.voice_settings`) の変換ユーティリティ — Issue #639。
 *
 * ## 設計
 *
 * - 日本語の話者は従来どおりトップレベルの `voice_id` が担う (後方互換)。
 * - 日本語以外の言語の話者を `voice_settings` に持つ。サーバは `voice_settings` を受け取ると
 *   トップレベル `voice_id` を `voice_settings.ja.voice_id` として読み替える。
 * - フォーム内では `Record<言語キー, VoiceId>` (空文字 = 既定話者) の単純な辞書で保持し、
 *   保存時に本ファイルの {@link buildVoiceSettingsPayload} で API 形式へ変換する。
 */
import type { LanguageVoiceSetting } from '../api/types';
import {
  DEFAULT_LANGUAGE_KEY,
  isVoiceIdValidForLanguage,
  POLLY_LANGUAGE_OPTIONS,
} from '../data/pollyVoices';

/** フォーム内で保持する言語別の話者 (`言語キー → VoiceId`。空文字 = その言語の既定話者)。 */
export type VoiceSelections = Record<string, string>;

/**
 * API の `voice_settings` を、フォーム用の辞書へ変換する。
 *
 * 日本語 (`ja`) はトップレベル `voice_id` 側で扱うため含めない。
 * その言語で使えない話者 (マスタに無い値) は取り除く。
 */
export function toVoiceSelections(
  voiceSettings: Record<string, LanguageVoiceSetting> | undefined | null,
): VoiceSelections {
  const selections: VoiceSelections = {};
  if (!voiceSettings || typeof voiceSettings !== 'object') return selections;
  for (const [languageKey, setting] of Object.entries(voiceSettings)) {
    if (languageKey === DEFAULT_LANGUAGE_KEY) continue;
    const voiceId = typeof setting?.voice_id === 'string' ? setting.voice_id.trim() : '';
    if (voiceId !== '' && isVoiceIdValidForLanguage(languageKey, voiceId)) {
      selections[languageKey] = voiceId;
    }
  }
  return selections;
}

/**
 * API の `voice_settings` から日本語の話者を取り出す (トップレベル `voice_id` が無い場合の補完用)。
 * 無い / 日本語で使えない話者の場合は空文字。
 */
export function japaneseVoiceFromSettings(
  voiceSettings: Record<string, LanguageVoiceSetting> | undefined | null,
): string {
  const voiceId = voiceSettings?.[DEFAULT_LANGUAGE_KEY]?.voice_id;
  if (typeof voiceId !== 'string') return '';
  const trimmed = voiceId.trim();
  return isVoiceIdValidForLanguage(DEFAULT_LANGUAGE_KEY, trimmed) ? trimmed : '';
}

/**
 * フォーム内の話者選択を、保存用の `voice_settings` へ変換する。
 *
 * - 日本語 (`ja`) は含めない (トップレベル `voice_id` を使う)。
 * - 放送言語 (`languages`) に含まれない言語の選択は捨てる
 *   (言語を外したあとに古い選択が保存され続けないようにする)。
 * - 空 / その言語で使えない話者は捨てる (サーバは不整合を 400 で拒否するため)。
 * - 有効な選択が 1 件も無ければ `undefined` (= 属性を作らない = 既存データと同じ形)。
 * - キー順はマスタ定義順に揃える。
 */
export function buildVoiceSettingsPayload(
  selections: VoiceSelections | undefined | null,
  languages: readonly string[],
): Record<string, LanguageVoiceSetting> | undefined {
  if (!selections || typeof selections !== 'object') return undefined;
  const payload: Record<string, LanguageVoiceSetting> = {};
  for (const option of POLLY_LANGUAGE_OPTIONS) {
    if (option.key === DEFAULT_LANGUAGE_KEY || !languages.includes(option.key)) continue;
    const voiceId = (selections[option.key] ?? '').trim();
    if (voiceId !== '' && isVoiceIdValidForLanguage(option.key, voiceId)) {
      payload[option.key] = { voice_id: voiceId };
    }
  }
  return Object.keys(payload).length > 0 ? payload : undefined;
}

/**
 * コンテンツ群で使われている放送言語の和集合をマスタ定義順で返す。
 * 未指定のコンテンツは日本語のみ扱い (`BroadcastPointForm` の `spotLanguages` と同じ規則)。
 */
export function collectContentLanguages(
  contents: ReadonlyArray<{ languages?: string[] }> | undefined | null,
): string[] {
  const keys = new Set<string>();
  for (const content of contents ?? []) {
    for (const language of content.languages ?? [DEFAULT_LANGUAGE_KEY]) keys.add(language);
  }
  return POLLY_LANGUAGE_OPTIONS.filter((option) => keys.has(option.key)).map(
    (option) => option.key,
  );
}

/**
 * プレビュー用: 言語ごとの実効話者を返す。
 * 日本語は親の `voice_id`、それ以外は {@link VoiceSelections}。未選択 / 不整合は `undefined`
 * (= サーバがその言語の既定話者を使う)。
 */
export function resolvePreviewVoiceId(
  languageKey: string,
  japaneseVoiceId: string | undefined,
  selections: VoiceSelections | undefined,
): string | undefined {
  const candidate =
    languageKey === DEFAULT_LANGUAGE_KEY ? japaneseVoiceId : selections?.[languageKey];
  return candidate && isVoiceIdValidForLanguage(languageKey, candidate) ? candidate : undefined;
}
